import {FeedFile, FeedFiles} from '@/api/models/FeedFiles.ts';
import ConfirmNavigationDialog from '@/components/ConfirmNavigationDialog.tsx';
import FormControls from '@/components/FormControls.tsx';
import Pending from '@/components/Pending.tsx';
import Title from '@/components/Title.tsx';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, FormGroup, InputGroup} from '@blueprintjs/core';
import {ChangeEvent, ReactElement, SyntheticEvent, useCallback, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router';

export default function FileEditorPage(): ReactElement {
	const {id} = useParams<'id'>();
	const file = useMemo(() => FeedFiles.get(parseInt(id!)), [id]);

	return (
		<>
			<Title title="Edit File" />

			<Pending resource={file}>
				{file => <Editor file={file} />}
			</Pending>
		</>
	);
}

interface EditorProps {
	file: FeedFile,
}

function Editor({file}: EditorProps): ReactElement {
	const navigate = useNavigate();

	const [dirty, setDirty] = useState(false);
	const [processing, setProcessing] = useState(false);

	const [name, setName] = useState(file.name ?? '');
	const onNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
		setName(event.currentTarget.value);
		setDirty(true);
	}, []);

	const onSubmit = useCallback(async (event: SyntheticEvent) => {
		event.stopPropagation();
		event.preventDefault();

		if (dirty) {
			setProcessing(true);

			try {
				await FeedFiles.update(file.id, {
					name,
				});
			} catch (error) {
				ToastErrors.fromError(error, 'Could not save feed file: ');
				return;
			} finally {
				setProcessing(false);
			}

			Toaster.success('File saved!');

			setDirty(false);
		}
		navigate(-1);
	}, [file, name, dirty]);

	const onCancelClick = useCallback(() => {
		navigate(-1);
	}, []);

	return (
		<>
			<form action="#" onSubmit={onSubmit}>
				<div style={{display: 'flex', gap: 10}}>
					<FormGroup label="Name" fill={true}>
						<InputGroup value={name} onChange={onNameChange} />
					</FormGroup>

					<FormGroup label="File Path" fill={true}>
						<InputGroup disabled={true} value={file.path} />
					</FormGroup>
				</div>

				<FormControls>
					<Button text="Cancel" disabled={processing} onClick={onCancelClick} />
					<Button type="submit" intent="primary" text="Save" onClick={onSubmit} loading={processing} />
				</FormControls>
			</form>

			<ConfirmNavigationDialog dirty={dirty} />
		</>
	);
}
